import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';

/** Delay before navigation to allow dialog close animation to complete */
const NAVIGATION_DELAY_MS = 100;

interface UseCmdKNavigationOptions {
  onOpenChange: (open: boolean) => void;
}

interface ModifierEvent {
  metaKey: boolean;
  ctrlKey: boolean;
}

/**
 * Shared navigation behavior for CmdK dialogs.
 * Handles query state, navigation with new tab support, and dialog state.
 */
export function useCmdKNavigation({ onOpenChange }: UseCmdKNavigationOptions) {
  const [query, setQuery] = useState('');
  const router = useRouter();
  const modifierRef = useRef(false);

  const trackMetaKey = useCallback((e: ModifierEvent) => {
    modifierRef.current = e.metaKey || e.ctrlKey;
  }, []);

  const wasModifierPressed = useCallback(() => {
    const value = modifierRef.current;
    modifierRef.current = false;
    return value;
  }, []);

  const navigate = useCallback(
    (href: string) => {
      const openInNewTab = wasModifierPressed();
      onOpenChange(false);
      setQuery('');
      setTimeout(() => {
        if (openInNewTab) {
          window.open(href, '_blank');
        } else {
          router.push(href);
        }
      }, NAVIGATION_DELAY_MS);
    },
    [router, onOpenChange, wasModifierPressed],
  );

  const handleOpenChange = useCallback(
    (value: boolean) => {
      onOpenChange(value);
      if (!value) {
        setQuery('');
      }
      modifierRef.current = false;
    },
    [onOpenChange],
  );

  return {
    query,
    setQuery,
    navigate,
    handleOpenChange,
    trackMetaKey,
    wasModifierPressed,
  };
}
